import {StringUtils} from "../../node_modules/igv-utils/src/index.js"
import {createSupplementaryAlignments} from "./supplementaryAlignment.js"
import {byteToUnsignedInt, getBaseModificationSets} from "./mods/baseModificationUtils.js"
import orientationTypes from "./orientationTypes.js"
import {HGVS} from "../genome/hgvs.js"
import {ClinVar} from "../genome/clinVar.js"


const READ_PAIRED_FLAG = 0x1
const PROPER_PAIR_FLAG = 0x2
const READ_UNMAPPED_FLAG = 0x4
const MATE_UNMAPPED_FLAG = 0x8
const READ_STRAND_FLAG = 0x10
const MATE_STRAND_FLAG = 0x20
const FIRST_OF_PAIR_FLAG = 0x40
const SECOND_OF_PAIR_FLAG = 0x80
const SECONDARY_ALIGNMNET_FLAG = 0x100
const READ_FAILS_VENDOR_QUALITY_CHECK_FLAG = 0x200
const DUPLICATE_READ_FLAG = 0x400
const SUPPLEMENTARY_ALIGNMENT_FLAG = 0x800
const ELEMENT_SIZE = {
    c: 1,
    C: 1,
    s: 2,
    S: 2,
    i: 4,
    I: 4,
    f: 4
}

const MAX_CIGAR = 50

/**
 * readName
 * chr
 * cigar
 * lengthOnRef
 * start
 * seq
 * qual
 * mq
 * strand
 * blocks
 */

class BamAlignment {

    constructor() {
        this.hidden = false
    }

    isMapped() {
        return (this.flags & READ_UNMAPPED_FLAG) === 0
    }

    isPaired() {
        return (this.flags & READ_PAIRED_FLAG) !== 0
    }

    isProperPair() {
        return (this.flags & PROPER_PAIR_FLAG) !== 0
    }

    isFirstOfPair() {
        return (this.flags & FIRST_OF_PAIR_FLAG) !== 0
    }

    isSecondOfPair() {
        return (this.flags & SECOND_OF_PAIR_FLAG) !== 0
    }

    isSecondary() {
        return (this.flags & SECONDARY_ALIGNMNET_FLAG) !== 0
    }

    isSupplementary() {
        return (this.flags & SUPPLEMENTARY_ALIGNMENT_FLAG) !== 0
    }

    isFailsVendorQualityCheck() {
        return (this.flags & READ_FAILS_VENDOR_QUALITY_CHECK_FLAG) !== 0
    }

    isDuplicate() {
        return (this.flags & DUPLICATE_READ_FLAG) !== 0
    }

    isMateMapped() {
        return (this.flags & MATE_UNMAPPED_FLAG) === 0
    }

    isNegativeStrand() {
        return (this.flags & READ_STRAND_FLAG) !== 0
    }

    isMateNegativeStrand() {
        return (this.flags & MATE_STRAND_FLAG) !== 0
    }

    hasTag(tag) {
        return this.tagDict.hasOwnProperty(tag)
    }

    getTag(key) {
        return this.tagDict[key]
    }


    /**
     * @returns a boolean indicating strand of first in pair, true for forward, false for reverse, and undefined
     * if this is not paired or is not first and mate is not mapped.
     */
    get firstOfPairStrand() {
        if (this.isPaired()) {
            if (this.isFirstOfPair()) {
                return this.strand
            } else if (this.isMateMapped()) {
                return this.mate.strand
            }
        }
        return undefined
    }


    /**
     * Does alignment (or alignment extended by soft clips) contain the genomic location?
     *
     * @param genomicLocation
     * @param showSoftClips
     * @returns {boolean|boolean}
     */
    containsLocation(genomicLocation, showSoftClips) {
        const s = showSoftClips ? this.scStart : this.start
        const l = showSoftClips ? this.scLengthOnRef : this.lengthOnRef
        return (genomicLocation >= s && genomicLocation <= (s + l))
    }

    /**
     * Return data to show in the popup.  Elements are either strings (for raw HTML) or
     * objects with name, value, borderTop properties.
     *
     * @param genomicLocation - 0-based genomic location
     * @param hiddenTags - Set of bam tags to hide
     * @param showTags - Set of bam tags to show (overrides hide/show rules)
     * @returns {*[]}
     */
    async popupData(genomicLocation, hiddenTags, showTags, refBase, genome) {

        // if the user clicks on a base next to an insertion, show just the
        // inserted bases in a popup (like in desktop IGV).
        const nameValues = []

        // Convert genomic location to int
        genomicLocation = Math.floor(genomicLocation)

        if (this.insertions) {

            const seq = this.seq

            for (let insertion of this.insertions) {
                var ins_start = insertion.start
                if (genomicLocation === ins_start || genomicLocation === ins_start - 1) {
                    nameValues.push({name: 'Insertion', value: seq.substr(insertion.seqOffset, insertion.len)})
                    nameValues.push({name: 'Location', value: ins_start})
                    return nameValues
                }
            }
        }

        nameValues.push({name: 'Read Name', value: this.readName})


        // HGVS annotations for variants, and ClinVar links if available
        const readBase = this.readBaseAt(genomicLocation)
        if (refBase) {
            if (readBase && readBase !== refBase && readBase !== '*') {
                const hgvsNotation = await HGVS.createHGVSAnnotation(genome, this.chr, genomicLocation, refBase, readBase)
                if (hgvsNotation) {
                    const clinVarURL = await ClinVar.getClinVarURL(hgvsNotation)
                    if (clinVarURL) {
                        nameValues.push({
                            name: 'ClinVar',
                            value: `<a href='${clinVarURL}' target='_blank'>${hgvsNotation}</a>`
                        })
                    } else {
                        nameValues.push({name: 'HGVS', value: hgvsNotation})
                    }
                }
            }
        }

        // Sample
        // Read group
        nameValues.push('<hr/>')

        // Add 1 to genomic location to map from 0-based computer units to user-based units
        nameValues.push({name: 'Alignment Start', value: StringUtils.numberFormatter(1 + this.start), borderTop: true})
        nameValues.push({name: 'Read Strand', value: (true === this.strand ? '(+)' : '(-)'), borderTop: true})

        // Abbreviate long cigar strings, keeping the beginning and end to show cliping
        let cigar = this.cigar
        if (cigar && cigar.length > MAX_CIGAR) {
            const half = MAX_CIGAR / 2
            cigar = `${cigar.substring(0, half - 2)} ... ${cigar.substring(cigar.length - half + 2)}`
        }
        nameValues.push({name: 'Cigar', value: cigar})

        nameValues.push({name: 'Mapping Quality', value: this.mq})
        nameValues.push({name: 'Secondary', value: yesNo(this.isSecondary())})
        nameValues.push({name: 'Supplementary', value: yesNo(this.isSupplementary())})
        nameValues.push({name: 'Duplicate', value: yesNo(this.isDuplicate())})
        nameValues.push({name: 'Failed QC', value: yesNo(this.isFailsVendorQualityCheck())})

        if (this.isPaired()) {
            nameValues.push('<hr/>')
            nameValues.push({name: 'First in Pair', value: !this.isSecondOfPair(), borderTop: true})
            nameValues.push({name: 'Mate is Mapped', value: yesNo(this.isMateMapped())})
            if (this.pairOrientation) {
                nameValues.push({name: 'Pair Orientation', value: this.pairOrientation})
            }
            if (this.isMateMapped()) {
                nameValues.push({name: 'Mate Chromosome', value: this.mate.chr})
                nameValues.push({name: 'Mate Start', value: (this.mate.position + 1)})
                nameValues.push({name: 'Mate Strand', value: (true === this.mate.strand ? '(+)' : '(-)')})
                nameValues.push({name: 'Insert Size', value: this.fragmentLength})
                // Mate Start
                // Mate Strand
                // Insert Size
            }
            // First in Pair
            // Pair Orientation

        }

        const tagDict = this.tagDict

        if (tagDict.hasOwnProperty('SA')) {
            nameValues.push('<hr/>')
            nameValues.push({name: 'Supplementary Alignments', value: ''})
            const sa = createSupplementaryAlignments(tagDict['SA'])
            if (sa) {
                nameValues.push('<ul>')
                for (let s of sa) {
                    nameValues.push(`<li>${s.printString()}</li>`)
                }
                nameValues.push('</ul>')
            }
        }

        nameValues.push('<hr/>')
        for (let key in tagDict) {
            if (showTags?.has(key)) {
                nameValues.push({name: key, value: tagDict[key]})
            } else if (showTags) {
                hiddenTags.add(key)
            } else if (!hiddenTags.has(key)) {
                nameValues.push({name: key, value: tagDict[key]})
            }
        }

        if (hiddenTags && hiddenTags.size > 0) {
            nameValues.push({name: 'Hidden Tags', value: Array.from(hiddenTags).join(", ")})
        }

        nameValues.push('<hr/>')
        nameValues.push({name: 'Genomic Location: ', value: StringUtils.numberFormatter(1 + genomicLocation)})
        nameValues.push({name: 'Read Base:', value: readBase})

        let quality = this.readBaseQualityAt(genomicLocation)
        nameValues.push({name: 'Base Quality:', value: 255 === quality ? "unknown" : quality})

        const bmSets = this.getBaseModificationSets()
        if (bmSets) {
            const i = this.positionToReadIndex(genomicLocation)
            if (undefined !== i) {
                let found = false
                for (let bmSet of bmSets) {
                    if (bmSet.containsPosition(i)) {
                        if (!found) {
                            nameValues.push('<hr/>')
                            nameValues.push('<b>Base modifications:</b>')
                            found = true
                        }
                        const lh = Math.round((100 / 255) * byteToUnsignedInt(bmSet.likelihoods.get(i)))
                        nameValues.push(`${bmSet.fullName()} @ likelihood =  ${lh}%`)
                    }
                }
            }
        }

        return nameValues


        function yesNo(bool) {
            return bool ? 'Yes' : 'No'
        }
    }

    readBaseAt(genomicLocation) {

        const block = this.blockAtGenomicLocation(genomicLocation)
        if (block) {
            if ("*" === this.seq) {
                return "*"
            } else {
                const idx = block.seqIndexAt(genomicLocation)
                // if (idx >= 0 && idx < this.seq.length) {
                return this.seq[idx]
                //  }
            }
        } else {
            return undefined
        }
    }

    readBaseQualityAt(genomicLocation) {

        const block = this.blockAtGenomicLocation(genomicLocation)
        if (block) {
            if ("*" === this.qual) {
                return undefined
            } else {
                const idx = block.seqIndexAt(genomicLocation)
                if (idx >= 0 && this.qual && idx < this.qual.length) {
                    return this.qual[idx]
                } else {
                    return undefined
                }
            }
        } else {
            return undefined
        }
    }

    gapSizeAt(genomicLocation) {
        if (this.gaps) {
            for (let gap of this.gaps) {
                if (genomicLocation >= gap.start && genomicLocation < gap.start + gap.len) {
                    return gap.len
                }
            }
        }
        return 0
    }

    /**
     * Return soft clipped blocks, if they exist, keyed by alignment end (left or right)
     */
    softClippedBlocks() {
        let left
        let right
        let interiorSeen
        for (let b of this.blocks) {
            if ('S' === b.type) {
                if (interiorSeen) {
                    right = b
                } else {
                    left = b
                }
            } else if ('H' !== b.type) {
                interiorSeen = true
            }
        }
        return {left, right}
    }

    getBaseModificationSets() {

        if (!this.baseModificationSets && (this.tagDict["MM"] || this.tagDict["Mm"])) {

            const mm = this.tagDict["MM"] || this.tagDict["Mm"]
            const ml = this.tagDict["ML"] || this.tagDict["Ml"]

            if (StringUtils.isString(mm) && (!ml || Array.isArray(ml))) { // minimal validation, 10X uses these reserved tags for something completely different
                if (mm.length === 0) {
                    this.baseModificationSets = EMPTY_SET
                } else {
                    //getBaseModificationSets(mm, ml, sequence, isNegativeStrand)
                    this.baseModificationSets = getBaseModificationSets(mm, ml, this.seq, this.isNegativeStrand())
                }
                //}
            }
        }
        return this.baseModificationSets
    }

    positionToReadIndex(position) {
        const block = this.blockAtGenomicLocation(position)
        if (block) {
            return (position - block.start) + block.seqOffset
        } else {
            return undefined
        }
    }

    blockAtGenomicLocation(genomicLocation) {

        const blocks = this.blocks
        for (let i = 0; i < blocks.length; i++) {
            const block = blocks[i]
            if (genomicLocation >= block.start && genomicLocation < block.start + block.len) {
                return block
            }
        }
        return undefined
    }

    /** Return the insertion at the specified genomic location, if it exists. Insertions are considered to be at the
     /* position of the first inserted base, so for example an insertion of "AAA" after position 100
     /* would be considered to be at position 101.
     */
    insertionAtGenomicLocation(genomicLocation) {
        if (this.insertions) {
            for (let insertion of this.insertions) {
                const ins_start = insertion.start
                if (genomicLocation === ins_start) {
                    return insertion
                }
            }
        }
    }

    getGroupValue(groupBy, expectedPairOrientation) {

        let tag, chr, pos
        if (groupBy.startsWith("tag:")) {
            tag = groupBy.substring(4)
            groupBy = "tag"
        } else if (groupBy.startsWith("base:") || groupBy.startsWith("insertion:")) {
            const tokens = groupBy.split(":")
            if (tokens.length === 3) {
                groupBy = tokens[0]
                chr = tokens[1]
                pos = Number.parseInt(tokens[2].replaceAll(",", "")) - 1
            }
        }

        switch (groupBy) {

            case 'strand':
                return this.strand ? '+' : '-'
            case 'firstOfPairStrand': {
                const strand = this.firstOfPairStrand
                return strand === undefined ? "" : strand ? '+' : '-'
            }
            case 'mateChr':
                return (this.mate && this.isMateMapped()) ? this.mate.chr : ""
            case 'pairOrientation':
                return orientationTypes[expectedPairOrientation][this.pairOrientation] || ""
            case 'chimeric':
                return this.hasTag('SA') ? "chimeric" : ""
            case 'supplementary':
                return this.isSupplementary() ? "supplementary" : ""
            case 'readOrder':
                if (this.isPaired() && this.isFirstOfPair()) {
                    return "first"
                } else if (this.isPaired() && this.isSecondOfPair()) {
                    return "second"
                } else {
                    return ""
                }
            case 'phase':
                return this.getTag('HP') || ""
            case 'tag':
                return this.getTag(tag) || ""
            case 'base':
                if (this.chr === chr &&
                    this.start <= pos &&
                    this.end > pos) {
                    const baseAtPos = this.readBaseAt(pos)
                    if (baseAtPos) {
                        return baseAtPos
                    } else {
                        return "GAP"
                    }
                } else {
                    return ""
                }
            case 'insertion':
                if (this.chr === chr &&
                    this.start <= pos &&
                    this.end > pos) {
                    const insertion = this.insertionAtGenomicLocation(pos)
                    return insertion ? this.seq.substring(insertion.seqOffset, insertion.seqOffset + insertion.len) : ""
                } else {
                    return ""
                }
            default:
                return undefined
        }
    }

}

const EMPTY_SET = new Set()


function readInt(ba, offset) {
    return (ba[offset + 3] << 24) | (ba[offset + 2] << 16) | (ba[offset + 1] << 8) | (ba[offset])
}

function readShort(ba, offset) {
    return (ba[offset + 1] << 8) | (ba[offset])
}

function readFloat(ba, offset) {
    const dataView = new DataView(ba.buffer)
    return dataView.getFloat32(offset)
}

function readInt8(ba, offset) {
    const dataView = new DataView(ba.buffer)
    return dataView.getInt8(offset)
}

function readUInt8(ba, offset) {
    const dataView = new DataView(ba.buffer)
    return dataView.getUint8(offset)
}


export default BamAlignment